import type {
    DivisionConfig,
    FlightConfig,
    PopulatedFlight,
    RegistrationLike,
} from "./flightPlacementTypes";

export interface PlaceRegistrationsAutoParams {
    /** All registrations to place. */
    registrations: RegistrationLike[];
    /** Division definitions (with optional gender/age/handicap filters). */
    divisions: DivisionConfig[];
    /** Effective flights (one per division or explicit list). */
    effectiveFlights: FlightConfig[];
    /** Populated flights to fill (we push players into .players). */
    populatedFlights: PopulatedFlight[];
    /** Assignment mode; when 'manual' we filter by division/flight name as fallback. */
    assignmentMode: string;
}

/**
 * Returns true if a registration matches a division's optional filters.
 * If a filter is not set on the division, it is considered to pass.
 */
function registrationMatchesDivision(registration: RegistrationLike, division: DivisionConfig): boolean {
    const genderOk =
        !division.divisionGender ||
        division.divisionGender.toLowerCase() === (registration.gender ?? "").toLowerCase();
    const minAgeOk =
        !division.divisionMinAge || division.divisionMinAge <= (registration.age ?? 0);
    const maxAgeOk =
        !division.divisionMaxAge || division.divisionMaxAge >= (registration.age ?? 0);
    const minHdcpOk =
        !division.divisionMinHdcp || division.divisionMinHdcp <= (registration.handicap ?? 0);
    const maxHdcpOk =
        !division.divisionMaxHdcp || division.divisionMaxHdcp >= (registration.handicap ?? 0);
    return genderOk && minAgeOk && maxAgeOk && minHdcpOk && maxHdcpOk;
}

/**
 * Returns true if a registration matches a flight's optional filters.
 * If a filter is not set on the flight, it is considered to pass.
 */
function registrationMatchesFlight(
    registration: RegistrationLike,
    flight: FlightConfig
): boolean {
    const genderOk =
        !flight.flightGender ||
        flight.flightGender.toLowerCase() === (registration.gender ?? "").toLowerCase();
    const minHdcpOk =
        !flight.flightMinHdcp || flight.flightMinHdcp <= (registration.handicap ?? 0);
    const maxHdcpOk =
        !flight.flightMaxHdcp || flight.flightMaxHdcp >= (registration.handicap ?? 0);
    const minAgeOk =
        !flight.flightMinAge || flight.flightMinAge <= (registration.age ?? 0);
    const maxAgeOk =
        !flight.flightMaxAge || flight.flightMaxAge >= (registration.age ?? 0);
    return genderOk && minHdcpOk && maxHdcpOk && minAgeOk && maxAgeOk;
}

/**
 * Places registrations into flights using automatic assignment by criteria.
 *
 * LOGIC (auto mode):
 * - For each division we get the flights that belong to it (effectiveFlights
 *   where divisionIndex === division.index).
 * - We filter registrations that match the division's optional filters
 *   (gender, age, handicap). If assignmentMode is 'manual' we instead
 *   filter by registration.division === division.divisionName (fallback).
 * - For each flight in that division we filter the division's registrations
 *   by the flight's optional filters (or by registration.flight === flight.flightName
 *   when assignmentMode === 'manual').
 * - We push the resulting registrations into the corresponding populated flight.
 */
export function placeRegistrationsAuto(params: PlaceRegistrationsAutoParams): void {
    const {
        registrations,
        divisions,
        effectiveFlights,
        populatedFlights,
        assignmentMode,
    } = params;

    divisions.forEach((division: DivisionConfig) => {
        const divFlights = effectiveFlights.filter(
            (flight: FlightConfig) => flight.divisionIndex === division.index
        );

        const divRegistrations =
            assignmentMode === "manual"
                ? registrations.filter(
                      (registration: RegistrationLike) =>
                          registration.division === division.divisionName
                  )
                : registrations.filter((registration: RegistrationLike) =>
                      registrationMatchesDivision(registration, division)
                  );

        divFlights.forEach((flight: FlightConfig) => {
            const flightRegistrations =
                assignmentMode === "manual"
                    ? divRegistrations.filter(
                          (registration: RegistrationLike) =>
                              registration.flight === flight.flightName
                      )
                    : divRegistrations.filter((registration: RegistrationLike) =>
                          registrationMatchesFlight(registration, flight)
                      );

            const popFlight = populatedFlights.find(
                (p: PopulatedFlight) => p.index === flight.index
            );
            if (popFlight) popFlight.players.push(...flightRegistrations);
        });
    });
}
